import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Send,
  CheckCircle2,
  AlertTriangle,
  Mail,
  Phone,
  MapPin,
  ChevronDown,
  ArrowLeft,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { CONTACT } from '@/constants/testIds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  interest: 'general',
  message: '',
};

// FAQ content — Heritage Estate tone. Direct, practical, honest.
// If the founder wants to edit copy later, this list is the only place.
const FAQ = [
  {
    q: 'How long does UK delivery take?',
    a: 'Every bike and every camping item we sell is drop-shipped straight from our manufacturing partners, so honest answer: allow 10 to 18 working days to your door. We don\u2019t hold stock in a warehouse, we hold accountability. If a batch is running slow, we tell you before you pay.',
  },
  {
    q: 'What warranty comes with the bikes?',
    a: 'A full 2-year manufacturer\u2019s warranty on the frame, motor and battery, covering defects in materials and workmanship under normal use. We handle the paperwork end for you \u2014 you deal with us, not a Chinese warranty portal at 3 in the morning. Faulty within 30 days and it\u2019s replaced, no debate.',
  },
  {
    q: 'Can I come for a test ride?',
    a: 'We\u2019re online-only \u2014 no showroom, no shop, no pop-up in a Costa car park. What we do offer is a 30-minute call (Zoom or phone) with the founder to talk through your terrain, your height, your budget, and any adaptive needs. It\u2019s worth more than sitting on a bike for five minutes on a Saturday.',
  },
  {
    q: 'How do I know a bike will fit me?',
    a: 'Tell us your inside-leg measurement, your riding history and where you plan to ride. Our range includes step-through frames, low stand-over geometry, and one-handed brake options for anyone riding with a prosthetic or limited grip. If we cannae find one that fits, we\u2019ll say so \u2014 and often we can point you to someone else who can.',
  },
  {
    q: 'How should I look after the battery?',
    a: 'Store it indoors when it\u2019s below freezing. Charge it to 80% for daily use, full charge before a long ride, and try never to leave it flat for weeks on end. Give it a top-up once a month if the bike\u2019s sitting through winter. Proper care doubles the useful life \u2014 easily 5 to 8 years, not 2.',
  },
  {
    q: 'What\u2019s your returns policy?',
    a: 'Under the Consumer Contracts Regulations 2013 you have a 14-day cooling-off period from delivery to change your mind, no reason required. Bladed items (the Field Knife) are excluded once opened, as is anything used off-road (mud voids returns \u2014 same as any bike shop). Faulty goods are covered separately under the Consumer Rights Act 2015. See our Conditions of Sale for the full picture.',
  },
];

function FaqItem({ idx, q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <li
      data-testid={CONTACT.faqItem(idx)}
      className="border-b border-[var(--brand-stone)] last:border-b-0"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={CONTACT.faqQuestion(idx)}
        className="w-full flex items-start justify-between gap-6 py-5 text-left group"
      >
        <span className="font-display text-lg lg:text-xl text-[var(--brand-forest)] leading-snug group-hover:text-[var(--brand-forest-deep)] transition-colors">
          {q}
        </span>
        <ChevronDown
          size={20}
          className={`shrink-0 mt-1 text-[var(--brand-gold)] transition-transform duration-300 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>
      {open && (
        <div
          data-testid={CONTACT.faqAnswer(idx)}
          className="pb-6 pr-8 text-[15px] leading-[1.75] text-[var(--brand-ink)]"
        >
          {a}
        </div>
      )}
    </li>
  );
}

export default function ContactPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState({ state: 'idle', message: '' });

  useEffect(() => {
    document.title = 'Contact \u00b7 EcoBike Camping Supplies Co.';
    window.scrollTo(0, 0);
  }, []);

  const onChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: 'sending', message: '' });
    try {
      await axios.post(`${API}/enquiries`, { ...form, source: 'contact_page' });
      setStatus({
        state: 'success',
        message:
          'Thank you \u2014 your note has landed with the Gamekeeper. Expect a reply within one working day.',
      });
      setForm(initialForm);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({
        state: 'error',
        message:
          typeof detail === 'string'
            ? detail
            : 'Sorry, your enquiry could not be sent. Please try again in a moment, or drop us a line directly at hello@ecobikecampingsuppliesco.com.',
      });
    }
  };

  return (
    <div
      data-testid={CONTACT.page}
      className="min-h-screen bg-[var(--brand-parchment)]"
    >
      <Navigation />

      {/* Hero — heritage tone, on parchment (solid nav) */}
      <header
        data-testid={CONTACT.hero}
        className="pt-44 lg:pt-52 pb-14 bg-[var(--brand-parchment-warm)] border-b border-[var(--brand-stone)]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[var(--brand-olive)] hover:text-[var(--brand-forest)] transition-colors"
          >
            <ArrowLeft size={14} /> Back to the shop
          </button>
          <div className="mt-6 max-w-4xl">
            <span className="font-eyebrow">Get in touch &middot; The estate replies</span>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--brand-forest)] leading-[1.05] tracking-tight">
              Send us a note.<br />
              <span className="italic">The Gamekeeper replies.</span>
            </h1>
            <p className="mt-6 text-base lg:text-lg text-[var(--brand-muted)] leading-relaxed max-w-2xl">
              Bike fit, adaptive consult, wholesale enquiry, or a straight question
              about which kit will actually last your trip &mdash; drop us a note below.
              No call centre, no scripts, no auto-responder loop. One reply from our
              team, inside a working day.
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Left column — form */}
        <section className="lg:col-span-7">
          <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--brand-forest-deep)]">
            The enquiry form
          </div>
          <h2 className="mt-2 font-display text-3xl lg:text-4xl text-[var(--brand-forest)] leading-tight">
            Tell us what you&rsquo;re after.
          </h2>
          <p className="mt-3 text-sm text-[var(--brand-muted)] max-w-lg leading-relaxed">
            The more you tell us up front &mdash; terrain, height, budget, any adaptive
            requirements &mdash; the more useful our first reply can be.
          </p>

          <form
            onSubmit={submit}
            data-testid={CONTACT.form}
            className="mt-8 bento p-8 lg:p-10 space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-[var(--brand-olive)] mb-2">
                  Full name *
                </label>
                <input
                  data-testid={CONTACT.name}
                  className="field"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  maxLength={120}
                  placeholder="Hamish Macdonald"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-[var(--brand-olive)] mb-2">
                  Email *
                </label>
                <input
                  data-testid={CONTACT.email}
                  className="field"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  required
                  placeholder="you@example.co.uk"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-[var(--brand-olive)] mb-2">
                  Phone{' '}
                  <span className="opacity-60 normal-case tracking-normal">
                    (optional)
                  </span>
                </label>
                <input
                  data-testid={CONTACT.phone}
                  className="field"
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  placeholder="+44 7..."
                  maxLength={40}
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-[var(--brand-olive)] mb-2">
                  Interest
                </label>
                <select
                  data-testid={CONTACT.interest}
                  className="field"
                  name="interest"
                  value={form.interest}
                  onChange={onChange}
                >
                  <option value="general">General</option>
                  <option value="e-bikes">E-Bikes</option>
                  <option value="camping">Camping Supplies</option>
                  <option value="adaptive">Adaptive Cycling</option>
                  <option value="wholesale">Wholesale / Trade</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-[var(--brand-olive)] mb-2">
                Subject *
              </label>
              <input
                data-testid={CONTACT.subject}
                className="field"
                name="subject"
                value={form.subject}
                onChange={onChange}
                required
                maxLength={160}
                placeholder="Adaptive bike fitting in March"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-[var(--brand-olive)] mb-2">
                Message *
              </label>
              <textarea
                data-testid={CONTACT.message}
                className="field"
                name="message"
                value={form.message}
                onChange={onChange}
                rows={6}
                required
                maxLength={4000}
                placeholder="Tell us a little about what you’re after — trails, terrain, riders, kit..."
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
              <button
                data-testid={CONTACT.submit}
                type="submit"
                disabled={status.state === 'sending'}
                className="btn-gold inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-semibold rounded-none whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status.state === 'sending' ? 'Sending…' : 'Send Enquiry'}
                <Send size={15} />
              </button>
              <p className="text-xs text-[var(--brand-muted)]">
                We respect your privacy. No marketing, ever. See our{' '}
                <Link to="/privacy" className="link-expand font-semibold">
                  Privacy Policy
                </Link>
                .
              </p>
            </div>

            {status.state === 'success' && (
              <div
                data-testid={CONTACT.success}
                className="flex items-start gap-3 bg-[var(--brand-olive)]/10 border border-[var(--brand-olive)]/30 p-4 text-sm text-[var(--brand-forest)]"
              >
                <CheckCircle2
                  size={18}
                  className="mt-0.5 text-[var(--brand-olive)]"
                />
                <span>{status.message}</span>
              </div>
            )}
            {status.state === 'error' && (
              <div
                data-testid={CONTACT.error}
                className="flex items-start gap-3 bg-red-50 border border-red-200 p-4 text-sm text-red-800"
              >
                <AlertTriangle size={18} className="mt-0.5" />
                <span>{status.message}</span>
              </div>
            )}
          </form>
        </section>

        {/* Right column — contact block + FAQ */}
        <aside className="lg:col-span-5 space-y-12">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--brand-forest-deep)]">
              How to reach us
            </div>
            <ul className="mt-5 space-y-5 text-sm">
              <li className="flex items-start gap-3 text-[var(--brand-ink)]">
                <MapPin
                  size={18}
                  className="mt-0.5 shrink-0 text-[var(--brand-olive)]"
                />
                <span>
                  Online only &mdash; based on the west coast<br />
                  Ayrshire, Scotland &middot; United Kingdom
                </span>
              </li>
              <li className="flex items-center gap-3 text-[var(--brand-ink)]">
                <Mail
                  size={18}
                  className="shrink-0 text-[var(--brand-olive)]"
                />
                <a
                  href="mailto:hello@ecobikecampingsuppliesco.com"
                  className="link-expand"
                >
                  hello@ecobikecampingsuppliesco.com
                </a>
              </li>
              <li className="flex items-start gap-3 text-[var(--brand-ink)]">
                <Phone
                  size={18}
                  className="mt-0.5 shrink-0 text-[var(--brand-olive)]"
                />
                <span>
                  Calls answered by our reception service.
                  <br />
                  <span className="text-[var(--brand-muted)] text-xs">
                    Drop us a note via the form and we&rsquo;ll ring you back
                    within one working day.
                  </span>
                </span>
              </li>
            </ul>
          </div>

          <div data-testid={CONTACT.faqSection}>
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--brand-forest-deep)]">
              Frequently asked
            </div>
            <h2 className="mt-2 font-display text-3xl lg:text-4xl text-[var(--brand-forest)] leading-tight">
              The questions we hear most.
            </h2>
            <p className="mt-3 text-sm text-[var(--brand-muted)] leading-relaxed">
              A few common ones answered up front. If yours isn&rsquo;t here, ask
              anyway &mdash; we don&rsquo;t hide behind an FAQ.
            </p>
            <ul className="mt-6 border-t border-[var(--brand-stone)]">
              {FAQ.map((f, i) => (
                <FaqItem key={i} idx={i} q={f.q} a={f.a} />
              ))}
            </ul>
          </div>
        </aside>
      </main>

      <Footer />
    </div>
  );
}
